/**
 * usePrefetchRoute
 *
 * Triggers an `import()` of a lazy route chunk on hover/focus so the chunk is
 * already in the cache by the time the user clicks. Eliminates the lazy-load
 * flash on slow networks without bundling the routes eagerly.
 *
 * Usage:
 *   const prefetch = usePrefetchRoute();
 *   <Link to="/jobs" {...prefetch(() => import("@/pages/Jobs"))}>Jobs</Link>
 *
 * The returned object spreads onMouseEnter / onFocus / onTouchStart handlers.
 * Each importer is invoked at most once per session (cached in a module Set).
 */
import { useCallback, useRef } from "react";

const fired = new WeakSet<() => Promise<unknown>>();

export type RoutePrefetchHandlers = {
  onMouseEnter: () => void;
  onFocus: () => void;
  onTouchStart: () => void;
};

export function usePrefetchRoute() {
  const inflight = useRef<Set<() => Promise<unknown>>>(new Set());

  return useCallback((importer: () => Promise<unknown>): RoutePrefetchHandlers => {
    const trigger = () => {
      if (fired.has(importer) || inflight.current.has(importer)) return;
      inflight.current.add(importer);
      // Defer to idle so we never compete with active rendering.
      const run = () => {
        importer()
          .then(() => {
            fired.add(importer);
          })
          .catch(() => {
            // Swallow — the real navigation will surface any error via Suspense.
          })
          .finally(() => {
            inflight.current.delete(importer);
          });
      };
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        (window as Window & {
          requestIdleCallback: (cb: () => void) => void;
        }).requestIdleCallback(run);
      } else {
        setTimeout(run, 50);
      }
    };

    return {
      onMouseEnter: trigger,
      onFocus: trigger,
      onTouchStart: trigger,
    };
  }, []);
}

export default usePrefetchRoute;
